import { roundMsToSeconds } from '~shared/timeutils'
import { GroupRunState } from '~shared/utils'

import type { GroupTiming } from './group.types'

export type GroupRunTimingView = {
  /**
   * `countdown` counts down to the projected end, `elapsed` shows what the
   * group used.
   */
  kind: 'countdown' | 'elapsed'
  ms: number
  overdue: boolean
  /** Slack before a late time turns red. */
  toleranceSeconds: number
}

/**
 * Decides whether a group shows a run timing row, and what it says.
 *
 * Returns null for an untouched future group, where the planned row already
 * says everything, and for an active group with no countdown yet.
 */
export function getGroupRunTimingView(
  groupTiming: GroupTiming | null,
): GroupRunTimingView | null {
  if (!groupTiming) return null

  const { state, countdown, actual, original, actualDiffersFromOriginal } =
    groupTiming

  if (state === GroupRunState.GROUP_ACTIVE) {
    if (countdown === null) return null

    return {
      kind: 'countdown',
      ms: countdown,
      overdue: roundMsToSeconds(countdown) <= 0,
      // A second of slack so a cue that has just started does not flicker red.
      toleranceSeconds: 1,
    }
  }

  if (state !== GroupRunState.GROUP_PAST && !actualDiffersFromOriginal) {
    return null
  }

  return {
    kind: 'elapsed',
    ms: actual.duration,
    // Math.round on both sides so this agrees with the duration on screen.
    overdue:
      roundMsToSeconds(actual.duration, Math.round) >
      roundMsToSeconds(original.duration, Math.round),
    toleranceSeconds: 0,
  }
}
